import { Camera, Mail, Save, Trash2, User } from 'lucide-react';
import { useEffect, useState, type ChangeEvent } from 'react';
import { authApi } from '../../api/auth';
import { type UserData } from '../../api/auth';
import { useAuth } from '../../contexts/AuthContext';
import { NotFoundImage } from '../../utils/function';
import { toast } from 'sonner';

export default function ProfilePage() {
  const { setUser } = useAuth();

  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState<UserData | null>(null);

  const getUserDetails = async () => {
    try {
      const response = await authApi.getCurrentUser()

      if (response.success) {
        setProfile(response.data)
      } else {
        setProfile(null)
      }

    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || "Failed to fetch user details")
    }
  }

  useEffect(() => {
    getUserDetails()
  }, [])

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setProfile(prev => {
      if (!prev) return null;
      return {
        ...prev,
        [name]: value
      };
    });
  };

  const handleImageUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfile(prev => {
          if (!prev) return null;
          return {
            ...prev,
            profile: reader.result as string,
            removedImage: false // Reset removedImage flag when new image is selected
          };
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setProfile(prev => {
      if (!prev) return null;
      return {
        ...prev,
        profile: '',
        removedImage: true
      };
    });
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile) return;

    setLoading(true);

    try {
      const formData = new FormData();

      // Add all profile fields except the profile image
      Object.entries(profile).forEach(([key, value]) => {
        // Skip the profile field as we'll handle it separately
        if (key !== 'profile' && value !== undefined && value !== null) {
          formData.append(key, String(value)); // Ensure all values are converted to strings
        }
      });

      // Handle profile image
      const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;

      if (profile.removedImage) {
        formData.append('profile', '');
      } else if (fileInput?.files?.[0]) {
        formData.append('profile', fileInput.files[0]);
      }


      const response = await authApi.updateProfile(formData);

      if (response.success) {
        setUser(response.data);
        setProfile(response.data);
        localStorage.setItem('user', JSON.stringify(response.data));

        toast.success('Profile updated successfully');
      } else {
        toast.error(response.message || 'Failed to update profile');
      }
    } catch (error) {
      console.log('Error updating profile:', error);
      toast.error('An error occurred while updating the profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-white tracking-tight">My Profile</h1>
        <p className="text-slate-400 mt-1">Manage your account settings and preferences.</p>
      </div>

      <form onSubmit={handleSave} className="space-y-8">
        {/* Profile Header */}
        <div className="bg-[#0B1623] border border-white/10 rounded-2xl p-8 shadow-xl flex flex-col sm:flex-row items-center gap-8">
          <div className="relative group">
            <div className="w-24 h-24 rounded-full bg-linear-to-br from-cyan-500 to-blue-600 p-[2px] relative group">
              <div className="w-full h-full rounded-full bg-black overflow-hidden">
                {profile?.profile ? (
                  <div className="relative group">
                    <img
                      src={profile.profile}
                      alt="Profile"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = NotFoundImage;
                      }}
                    />
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveImage();
                      }}
                      hidden={!profile.profile}
                      className="cursor-pointer absolute inset-0 flex items-center justify-center bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity rounded-full"
                      title="Remove profile picture"
                    >
                      <Trash2 className="w-5 h-5 text-white" />
                    </button>
                  </div>
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gray-800 rounded-full">
                    <img
                      src={NotFoundImage}
                      alt="Profile not found"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = NotFoundImage;
                      }}
                    />
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  id="profile-image-upload"
                />
              </div>
            </div>
            <label
              htmlFor="profile-image-upload"
              className="absolute bottom-0 right-0 p-2 bg-cyan-600 rounded-full text-white shadow-lg hover:bg-cyan-500 transition-colors cursor-pointer"
            >
              <Camera className="w-4 h-4" />
            </label>
          </div>
          <div className="text-center sm:text-left">
            <h2 className="text-2xl font-bold text-white">{profile?.name ?? '-'}</h2>
            <p className="text-cyan-400 font-medium">{profile?.role.toUpperCase() ?? '-'}</p>
            <p className="text-slate-500 text-sm mt-1">
              Member since {profile?.createdAt ? new Date(profile?.createdAt).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '-'}
            </p>
          </div>
        </div>

        {/* Personal Info */}
        <section className="bg-[#0B1623] border border-white/10 rounded-2xl p-8 shadow-xl">
          <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
            <User className="w-5 h-5 text-cyan-400" />
            Personal Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-2">Full Name</label>
              <input
                type="text"
                name="name"
                value={profile?.name ?? '-'}
                onChange={handleInputChange}
                className="w-full px-4 py-3 bg-black/20 border border-white/10 rounded-xl focus:border-cyan-500 text-white outline-none transition-colors"
              />
            </div>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-2">Email Address</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <input
                    type="email"
                    name="email"
                    value={profile?.email ?? '-'}
                    disabled
                    onChange={handleInputChange}
                    className="w-full pl-12 pr-4 py-3 bg-black/20 border border-white/10 rounded-xl focus:border-cyan-500 text-white outline-none transition-colors"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="flex justify-end pt-4">
          <button
            type="submit"
            disabled={loading}
            className="flex items-center gap-2 bg-linear-to-r from-cyan-600 to-blue-600 text-white px-8 py-4 rounded-xl hover:from-cyan-500 hover:to-blue-500 transition-all font-bold text-lg shadow-lg shadow-cyan-900/20 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className="w-5 h-5" />
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
}
